import { MapLike } from '../core'
import { AppValueTypes } from '../common'

export enum AttributeValidationError {
  OUTSIDE_RANGE = 'OUTSIDE_RANGE',
  NOT_NUMBER = 'NOT_NUMBER',
  INVALID_MEMBERS = 'INVALID_MEMBERS',
  NOT_PRESENT = 'NOT_PRESENT',
  NOT_BOOLEAN = 'NOT_BOOLEAN',
  NOT_ARRAY = 'NOT_ARRAY',
  NOT_ENUM_VALUE = 'NOT_ENUM_VALUE',
}

export type AttributeValidationResult = {
  isValid: boolean
  error: AttributeValidationError | null
}

export type AttributeValidationNumberArray = AttributeValidationResult & {
  isValid: boolean
  error: AttributeValidationError | null
  invalidIndexes: number[]
}

export type AttributeValidationPresent = {}

type UpperBound = {
  max: number
}

type LowerBound = {
  min: number
}

export type AttributeValueRange = Partial<LowerBound & UpperBound>

export interface IAttributeNumberValueValidator {
  (value: number, range: AttributeValueRange): AttributeValidationResult
}

export interface IAttributeNumberArrayValidator {
  (arr: number[], range: AttributeValueRange, start?: number, end?: number): AttributeValidationNumberArray
}

export const verify = 0x5a

export type AttributeType = 'map' | 'number' | 'array' | 'boolean' | 'enumeration' | 'present'
export type AttributeKeyRangeDefinition = { attributeType: 'number'; range: Required<AttributeValueRange> }
export type AttributeKeyArrayRangeDefinition = { attributeType: 'array'; range: AttributeValueRange; start?: number; end?: number }
export type AttributeKeyEnumDefinition = { attributeType: 'enumeration'; enumeration: MapLike<string | number> }
export type AttributeKeyBooleanDefinition = { attributeType: 'boolean' }
export type AttributeKeyPresentDefinition = { attributeType: 'present' }
export type AttributeKeyDefinition<T extends { attributeType: AttributeType }> = T extends { attributeType: 'enumeration' }
  ? AttributeKeyEnumDefinition
  : T extends { attributeType: 'number' }
    ? AttributeKeyRangeDefinition
    : T extends { attributeType: 'boolean' }
      ? AttributeKeyBooleanDefinition
      : T extends { attributeType: 'array' }
        ? AttributeKeyArrayRangeDefinition
        : T extends { attributeType: 'present' }
          ? AttributeKeyPresentDefinition
          : never

const attx: AttributeKeyDefinition<AttributeKeyEnumDefinition> = {
  attributeType: 'enumeration',
  enumeration: AppValueTypes,
}

export type ValidAttributeKeyDefinition =
  | AttributeKeyDefinition<AttributeKeyRangeDefinition>
  | AttributeKeyDefinition<AttributeKeyEnumDefinition>
  | AttributeKeyDefinition<AttributeKeyBooleanDefinition>
  | AttributeKeyDefinition<AttributeKeyArrayRangeDefinition>
  | AttributeKeyDefinition<AttributeKeyPresentDefinition>

/* possibly the second part of union should be: T[k][k2] extends number | string .... ? never : ValidAttributeKeyDefinition
    #rainyday
*/
export type AttributeRangeDefinition<T> = { [k in keyof T]: ValidAttributeKeyDefinition | { [k2 in keyof T[k]]: ValidAttributeKeyDefinition } }

type PartialObjectAttributeValidator<T> = Partial<{ [k in keyof T]: IAttributeNumberValueValidator | { [k2 in keyof T[k]]: IAttributeNumberValueValidator } }>

export type ValidationMap<T> = { [k in keyof T]: AttributeValidationResult | { [k2 in keyof T[k]]: AttributeValidationResult } }
